import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";

type TemplatePreference = {
  trace_id: string;
  name: string;
  configurable: boolean;
  enabled: boolean;
  source: { id: string; name: string } | null;
};
type Preferences = {
  subject: string;
  default_enabled: boolean;
  manual_enabled: boolean;
  manual_source: { id: string; name: string } | null;
  templates: TemplatePreference[];
};

export default function MailPreferencesForm({
  subjectId,
  templateTraceId,
}: {
  subjectId?: string;
  templateTraceId?: string;
}) {
  const { t, i18n } = useTranslation();
  const { handleError, errorModal } = useErrorModal();
  const [data, setData] = useState<Preferences | null>(null);
  const [saving, setSaving] = useState(false);
  const activeScope = useRef({ active: false });
  const inheritedManualSource =
    data?.manual_source && data.manual_source.id !== data.subject
      ? data.manual_source
      : null;
  useEffect(() => {
    const scope = { active: true };
    activeScope.current = scope;
    setData(null);
    setSaving(false);
    api.genericApiRequest({
      method: "get",
      entity: "mail/preferences",
      entityId: subjectId ?? "me",
      shouldNotImpersonate: true,
      successHandler: (response: { data: Preferences }) => {
        if (scope.active) setData(response.data);
      },
      failHandler: (error: unknown) => {
        if (scope.active) handleError(error);
      },
    });
    return () => {
      scope.active = false;
    };
  }, [subjectId, handleError]);

  const save = (
    enabled: boolean,
    options: { template_trace_id?: string; setting?: "manual" | "all" } = {}
  ) => {
    const scope = activeScope.current;
    setSaving(true);
    api.genericApiRequest({
      method: "put",
      entity: "mail/preferences",
      entityId: subjectId ?? "me",
      shouldNotImpersonate: true,
      submitData: {
        enabled,
        ...options,
      },
      successHandler: (response: { data: Preferences }) => {
        if (scope.active) {
          setData(response.data);
          setSaving(false);
        }
      },
      failHandler: (error: unknown) => {
        if (scope.active) {
          setSaving(false);
          handleError(error);
        }
      },
    });
  };
  if (!subjectId) {
    const locallyEnabled =
      data !== null &&
      (data.default_enabled ||
        data.manual_enabled ||
        data.templates.some(
          (item) =>
            item.configurable &&
            (item.enabled ||
              (item.source !== null && item.source.id !== data.subject))
        ));
    return (
      <>
        {data && (
          <>
            <FormControlLabel
              label={t("mail_preferences_all")}
              control={
                <Switch
                  checked={locallyEnabled}
                  disabled={saving}
                  onChange={(_, checked) => save(checked, { setting: "all" })}
                />
              }
            />
            {!locallyEnabled && (
              <Typography variant="body2" color="text.secondary">
                {t("mail_preferences_system_hint")}
              </Typography>
            )}
          </>
        )}
        {errorModal}
      </>
    );
  }
  const columns: MRT_ColumnDef<TemplatePreference>[] = [
    { accessorKey: "name", header: t("mail_template") },
    {
      id: "enabled",
      header: t("mail_preferences_automatic"),
      Cell: ({ row }) => {
        const item = row.original;
        const inherited =
          item.source !== null && item.source.id !== data?.subject;
        return (
          <>
            <Switch
              checked={item.enabled}
              disabled={saving || !item.configurable || inherited}
              inputProps={{ "aria-label": item.name }}
              onChange={(_, checked) =>
                save(checked, { template_trace_id: item.trace_id })
              }
            />
            <Typography variant="caption">
              {!item.configurable
                ? t("mail_preferences_access_exception")
                : item.source
                ? t(
                    inherited
                      ? "mail_preferences_inherited"
                      : "mail_preferences_local",
                    { source: item.source.name }
                  )
                : ""}
            </Typography>
          </>
        );
      },
    },
  ];
  return (
    <>
      <Alert severity="info">{t("mail_preferences_explanation")}</Alert>
      {data && (
        <>
          {!templateTraceId && (
            <>
              <Stack direction="row" spacing={1}>
                <Button
                  disabled={saving}
                  onClick={() => save(true, { setting: "all" })}
                >
                  {t("mail_preferences_enable_all")}
                </Button>
                <Button
                  disabled={saving}
                  onClick={() => save(false, { setting: "all" })}
                >
                  {t("mail_preferences_disable_all")}
                </Button>
              </Stack>
              <FormControlLabel
                label={t("mail_preferences_manual")}
                control={
                  <Switch
                    checked={data.manual_enabled && data.manual_source === null}
                    disabled={saving || inheritedManualSource !== null}
                    onChange={(_, checked) =>
                      save(checked, { setting: "manual" })
                    }
                  />
                }
              />
              {inheritedManualSource && (
                <Typography variant="caption">
                  {t("mail_preferences_inherited", {
                    source: inheritedManualSource.name,
                  })}
                </Typography>
              )}
              <FormControlLabel
                label={t("mail_preferences_default")}
                control={
                  <Switch
                    checked={data.default_enabled}
                    disabled={saving}
                    onChange={(_, checked) => save(checked)}
                  />
                }
              />
            </>
          )}
          <MaterialReactTable
            columns={columns}
            data={data.templates.filter(
              (item) => !templateTraceId || item.trace_id === templateTraceId
            )}
            localization={
              i18n.language.startsWith("en")
                ? MRT_Localization_EN
                : MRT_Localization_DE
            }
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            initialState={{
              density: "compact",
              pagination: { pageIndex: 0, pageSize: 10 },
            }}
          />
        </>
      )}
      {errorModal}
    </>
  );
}
